import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import DevicesIcon from '@mui/icons-material/Devices';
import HomeIcon from '@mui/icons-material/Home';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface DeviceInfo {
  deviceType: string;
  deviceModel: string;
}

interface FormState {
  name: string;
  phone: string;
  devices: DeviceInfo[];
  pickupAddress: string;
  pickupAddressDetail: string;
  storeCode: string;
  bankName: string;
  bankAccount: string;
  bankOwner: string;
}

interface PickupProcessDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  formData: FormState;
}

const PickupProcessDialog: React.FC<PickupProcessDialogProps> = ({
  open,
  onClose,
  onConfirm,
  formData,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // 중복 클릭 방지용 상태/레퍼런스
  // - hasConfirmedRef: 렌더 주기와 무관하게 동기적으로 한번만 동작하도록 보장
  // - isSubmitting: 버튼 UI 비활성/로딩 표시용
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const hasConfirmedRef = React.useRef(false);
  const prevOpenRef = React.useRef(open);

  // 다이얼로그가 다시 열릴 때에만 잠금을 초기화하여 닫히는 애니메이션 중 중복 클릭을 차단
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      hasConfirmedRef.current = false;
      setIsSubmitting(false);
    }
    prevOpenRef.current = open;
  }, [open]);

  // 매입신청 완료 클릭 핸들러: 초고속 더블클릭에도 1회만 처리되도록 가드
  const handleConfirm = React.useCallback(async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (isSubmitting || hasConfirmedRef.current) return;
    hasConfirmedRef.current = true;
    setIsSubmitting(true);

    try {
      await onConfirm();
    } catch (error) {
      hasConfirmedRef.current = false;
      setIsSubmitting(false);
      console.error('[PickupProcessDialog] onConfirm failed', error);
    }
  }, [isSubmitting, onConfirm]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 5 },
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(99,102,241,0.1)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 8px 32px rgba(99,102,241,0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #6366f1 0%, #0ea5e9 50%, #8b5cf6 100%)',
          },
        }
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 1.5, sm: 3 },
          background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(14,165,233,0.06) 100%)',
          borderBottom: '1px solid rgba(99,102,241,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant={fullScreen ? 'h6' : 'h5'}
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          📋 매입신청 최종 확인
        </Typography>
        <IconButton onClick={onClose} size={fullScreen ? 'small' : 'medium'}>
          <CloseIcon fontSize={fullScreen ? 'small' : 'medium'} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        <Grid container>
          {/* 좌측 정보 확인 패널 */}
          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              borderRight: { lg: '1px solid rgba(99,102,241,0.1)' }
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 4 }, overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 22 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151' }}>
                  신청 정보 확인
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* 1) 고객 정보 */}
                <Grid item xs={6} sm={12}>
                  <Card sx={{
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.02) 0%, rgba(14,165,233,0.02) 100%)',
                    border: '1px solid rgba(99,102,241,0.1)',
                    boxShadow: '0 2px 8px rgba(99,102,241,0.05)',
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ color: '#6366f1', fontSize: 20, mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          고객 정보
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">이름</Typography>
                        <Typography variant="body2" fontWeight={600}>{formData.name || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">전화번호</Typography>
                        <Typography variant="body2" fontWeight={600}>{formData.phone || '-'}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 2) 반납 기기 */}
                <Grid item xs={12}>
                  <Card sx={{
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(14,165,233,0.02) 0%, rgba(6,182,212,0.02) 100%)',
                    border: '1px solid rgba(14,165,233,0.1)',
                    boxShadow: '0 2px 8px rgba(14,165,233,0.05)',
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <DevicesIcon sx={{ color: '#0ea5e9', fontSize: 20, mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          반납 기기 ({formData.devices.length}개)
                        </Typography>
                      </Box>
                      <Box sx={{ maxHeight: 'none' }}>
                        {formData.devices.length > 0 ? formData.devices.map((d, i) => {
                          // 태블릿 파싱 로직 (PurchaseForm과 동일)
                          const deviceParts = d.deviceModel.split(' - ');
                          let modelName = '';
                          let storage = '';
                          let connectivity = '';
                          let grade = '';
                          
                          if (d.deviceType === '태블릿') {
                            if (deviceParts.length >= 3) {
                              const lastPart = deviceParts[deviceParts.length - 1];
                              if (/^(Wi-Fi|Wi-Fi \+ Cellular)$/i.test(lastPart)) {
                                connectivity = lastPart;
                                const remainingParts = deviceParts.slice(0, -1);
                                const storagePattern = /(\d+(?:\.\d+)?\s*(GB|TB))/i;
                                for (let j = remainingParts.length - 1; j >= 0; j--) {
                                  if (storagePattern.test(remainingParts[j])) {
                                    storage = remainingParts[j];
                                    break;
                                  }
                                }
                                modelName = remainingParts[0] || d.deviceModel;
                              } else {
                                modelName = deviceParts[0];
                                if (/(\d+(?:\.\d+)?\s*(GB|TB))/i.test(deviceParts[1])) {
                                  storage = deviceParts[1];
                                }
                              }
                            } else {
                              modelName = deviceParts[0] || d.deviceModel;
                            }
                            if ((d as any).userSelectedGrade) {
                              grade = String((d as any).userSelectedGrade) + '급';
                            }
                          } else {
                            modelName = deviceParts.length > 1 ? deviceParts[0] : d.deviceModel;
                            const secondPart = deviceParts.length > 1 ? deviceParts[1] : '';
                            const storageRegex = /^(\d+GB|\d+TB|1TB)$/i;
                            if (storageRegex.test(secondPart)) {
                              storage = secondPart;
                            }
                            if ((d as any).userSelectedGrade) {
                              grade = String((d as any).userSelectedGrade) + '급';
                            }
                          }
                          
                          return (
                            <Box key={i} sx={{ mb: i < formData.devices.length - 1 ? 1 : 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={d.deviceType}
                                  size="small"
                                  sx={{
                                    background: 'rgba(14,165,233,0.1)',
                                    color: '#0369a1',
                                    fontWeight: 600,
                                  }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                                  {modelName} {storage}
                                </Typography>
                                {/* 등급 CHIP */}
                                {grade && (() => {
                                  const gradeOnly = grade.replace('급', '');
                                  const colorMap: Record<string, { fg: string; bg: string }> = {
                                    A: { fg: '#10b981', bg: 'rgba(16,185,129,0.12)' },
                                    B: { fg: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
                                    C: { fg: '#f59e0b', bg: 'rgba(245,158,11,0.14)' },
                                    D: { fg: '#ef4444', bg: 'rgba(239,68,68,0.14)' },
                                  };
                                  const { fg, bg } = colorMap[gradeOnly] || colorMap.B;
                                  return (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      sx={{
                                        color: fg,
                                        fontWeight: 900,
                                        backgroundColor: bg,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 1,
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      {grade}
                                    </Typography>
                                  );
                                })()}
                                {/* 모델 유형 (연결 방식) */}
                                {connectivity && (
                                  <Typography 
                                    component="span"
                                    variant="caption" 
                                    sx={{ 
                                      color: '#3b82f6', fontWeight: 800,
                                      backgroundColor: 'rgba(59, 130, 246, 0.1)', px: 0.75, py: 0.25, borderRadius: 1,
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    {connectivity}
                                  </Typography>
                                )}
                                {/* 예상금액 */}
                                {(d as any) && (d as any).estimatedPrice !== undefined && (
                                  <Typography 
                                    component="span"
                                    variant="caption" 
                                    sx={{ 
                                      color: '#374151', fontWeight: 700,
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    예상금액: {(d as any).estimatedPrice > 0 ? `₩${((d as any).estimatedPrice).toLocaleString()}` : '가격 미설정'}
                                  </Typography>
                                )}
                              </Box>
                              {i < formData.devices.length - 1 && <Divider sx={{ my: 0.5 }} />}
                            </Box>
                          );
                        }) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            선택된 기기가 없습니다
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 3) 수거 주소 */}
                <Grid item xs={6} sm={12}>
                  <Card sx={{
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(16,185,129,0.02) 0%, rgba(5,150,105,0.02) 100%)',
                    border: '1px solid rgba(16,185,129,0.1)',
                    boxShadow: '0 2px 8px rgba(16,185,129,0.05)',
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <HomeIcon sx={{ color: '#10b981', fontSize: 20, mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          수거 주소
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formData.pickupAddress || '주소가 없습니다'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formData.pickupAddressDetail || '상세 주소가 없습니다'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 4) 계좌 & 매장정보 */}
                <Grid item xs={6} sm={12}>
                  <Card sx={{
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(245,158,11,0.02) 0%, rgba(217,119,6,0.02) 100%)',
                    border: '1px solid rgba(245,158,11,0.1)',
                    boxShadow: '0 2px 8px rgba(245,158,11,0.05)',
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccountBalanceIcon sx={{ color: '#f59e0b', fontSize: 20, mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          계좌 & 매장정보
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {formData.bankName || '-'} {formData.bankAccount || '-'}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}예금주: {formData.name || '-'}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        매장 코드: <strong>{formData.storeCode || '미입력'}</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* 우측 수거 프로세스 안내 패널 */}
          <Grid item xs={12} lg={6}>
            <Box sx={{
              p: { xs: 2, sm: 4 },
              overflowY: 'auto',
              background: 'linear-gradient(135deg, rgba(99,102,241,0.01) 0%, rgba(14,165,233,0.01) 100%)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2,
                }}>
                  <Typography sx={{ fontSize: '1.2rem' }}>🚚</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  수거 프로세스 안내
                </Typography>
              </Box>
              <Box>
                {/* Step 1 */}
                <Box sx={{
                  display: 'flex', alignItems: 'flex-start', mb: 3, position: 'relative',
                  '&::after': {
                    content: '""', position: 'absolute',
                    left: 20, top: 45,
                    width: 2, height: 'calc(100% + 20px)',
                    background: 'linear-gradient(180deg, #6366f1 0%, #0ea5e9 100%)',
                    opacity: 0.2,
                  }
                }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(99,102,241,0.3)', mr: 2,
                  }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>📦</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} mb={1}>
                      택배 방문 예정
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      CJ대한통운 택배기사가 <strong>영업일 기준 1~3일 이내</strong> 방문 예정입니다
                    </Typography>
                    <Box sx={{
                      p: 2, borderRadius: 2,
                      background: 'rgba(99,102,241,0.05)',
                      border: '1px solid rgba(99,102,241,0.15)',
                    }}>
                      <Typography variant="body2" color="primary.main" fontWeight={600}>
                        📋 "다원트레이드" 업체명을 꼭 확인해주세요
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* Step 2 */}
                <Box sx={{
                  display: 'flex', alignItems: 'flex-start', mb: 3, position: 'relative',
                  '&::after': {
                    content: '""', position: 'absolute',
                    left: 20, top: 45,
                    width: 2, height: 'calc(100% + 20px)',
                    background: 'linear-gradient(180deg, #0ea5e9 0%, #ef4444 100%)',
                    opacity: 0.2,
                  }
                }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(14,165,233,0.3)', mr: 2,
                  }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>📦</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} mb={1}>
                      안전 포장 필수
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      원활한 수거를 위해 <strong>포장을 미리 완료</strong>해주세요
                    </Typography>
                    <Box sx={{
                      p: 2, borderRadius: 2,
                      background: 'rgba(14,165,233,0.05)',
                      border: '1px solid rgba(14,165,233,0.15)',
                    }}>
                      <Typography variant="body2" color="#0369a1" fontWeight={600}>
                        🛡️ 완충제를 충분히 동봉해주세요
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* Warning */}
                <Box sx={{
                  display: 'flex', alignItems: 'flex-start',
                  p: 2.5, borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(251,146,60,0.05) 100%)',
                  border: '1px solid rgba(239,68,68,0.15)',
                }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2,
                  }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>⚠️</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" color="error.main" fontWeight={700} mb={1}>
                      중요 안내사항
                    </Typography>
                    <Typography variant="body2" color="error.dark" fontWeight={600}>
                      택배 포장이 되지 않은 경우 수거 불가능하며 접수는 자동 취소됩니다
                    </Typography>
                  </Box>
                </Box>
              {/* Pricing notice */}
              <Box sx={{
                mt: 3,
                p: 2.5,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                background: 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(14,165,233,0.06) 100%)',
                border: '1px solid rgba(59,130,246,0.2)'
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #0ea5e9 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: '1.05rem', color: 'white' }}>ℹ️</Typography>
                </Box>
                <Typography variant="body2" fontWeight={600} color="#1e40af">
                  가격은 개별 안내 방식으로 진행되며, 동일 모델을 여러 대 신청하신 경우 시리얼 넘버 확인을 부탁드립니다
                </Typography>
              </Box>
                {/* Final notice */}
                <Box sx={{
                  mt: 3, p: 2.5, borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(5,150,105,0.05) 100%)',
                  border: '1px solid rgba(16,185,129,0.2)', textAlign: 'center',
                }}>
                  <CheckCircleIcon sx={{ color: '#059669', fontSize: 28, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600} color="#065f46">
                    위 정보와 수거 프로세스를 모두 확인하셨다면<br/>
                    <strong>매입신청을 완료</strong>해주세요
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 4 },
        py: { xs: 1.5, sm: 3 },
        borderTop: '1px solid rgba(99,102,241,0.1)',
        justifyContent: 'space-between',
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size={fullScreen ? 'small' : 'medium'}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1,
            borderColor: 'rgba(99,102,241,0.3)',
            color: '#6366f1',
            '&:hover': {
              borderColor: '#6366f1',
              background: 'rgba(99,102,241,0.05)',
            }
          }}
        >
          취소
        </Button>

        <Button
          onClick={handleConfirm}
          variant="contained"
          size={fullScreen ? 'medium' : 'large'}
          sx={{
            fontWeight: 700,
            borderRadius: 3,
            px: { xs: 4, sm: 5 },
            py: 1,
            background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
            boxShadow: '0 6px 20px rgba(99,102,241,0.3)',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            '&:hover': {
              background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)',
              boxShadow: '0 8px 25px rgba(99,102,241,0.4)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          }}
          disabled={isSubmitting}
          disableRipple={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {isSubmitting ? '처리중…' : '✅ 매입신청 완료'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PickupProcessDialog;
